/// <reference lib="dom" />

/**
 * worldTileset.ts
 * Per-world tileset-image picker.
 *
 * Resolves the active tileset PNG for a given {@link WorldId} from a
 * loaded {@link RenderAssets} bundle. Worlds without bespoke tile art
 * fall back to the World 1 Sunmeadow tileset so an unfinished world
 * still renders something instead of blank tiles.
 *
 * Tile semantics are intentionally cross-world — every world variant
 * uses the same `firstgid` + grid layout as W1 so level files (which
 * reference tile IDs by position in the W1 atlas) render identical
 * geometry regardless of biome.
 *
 * @module worldTileset
 */

import type { WorldId } from '../../constants/gameConstants';
import type { RenderAssets } from './RenderAssets';

/**
 * Pick the active tileset image for `worldId` from `assets`.
 *
 * @param worldId - World currently being rendered.
 * @param assets  - Loaded asset bundle.
 * @returns The world-specific tileset PNG, or the W1 fallback.
 */
export function pickTileset(worldId: WorldId, assets: RenderAssets): HTMLImageElement {
  if (worldId === 3) return assets.world3TilesetImg;
  if (worldId === 4) return assets.world4TilesetImg;
  if (worldId === 5) return assets.world5TilesetImg;
  // DECISION: W2 has no platforming tilemap (the space shmup uses its own
  // background system), so it falls back to the W1 Sunmeadow atlas. Every
  // other world now ships its own bespoke tile art.
  return assets.tilesetImg;
}
